/**
 * useRequests — relationship-based request/workflow system.
 *
 * Supports the full lifecycle: submit → acknowledge → in_progress → resolve/close
 * with threaded comments, role-based visibility, and notification triggers.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { insertNotification, notifyOrgManagers } from "@/lib/notifications";

// ─── Types ────────────────────────────────────────────────────────────────────

export type RequestType =
  | "maintenance" | "dispute" | "amenity" | "document" | "inquiry" | "compliance";

export type RequestStatus =
  | "new" | "acknowledged" | "in_progress" | "pending_info" | "resolved" | "closed" | "rejected";

export type RequestRelationship =
  | "guest_to_host" | "host_to_pm" | "host_to_strata" | "pm_to_strata" | "strata_to_host" | "pm_to_host";

export type RequestPriority = "low" | "normal" | "high" | "urgent";

export interface Request {
  id: string;
  org_id: string;
  type: RequestType;
  context_relationship: RequestRelationship;
  priority: RequestPriority;
  status: RequestStatus;
  submitted_by_user_id: string | null;
  submitted_by_guest_token_id: string | null;
  submitted_by_name: string | null;
  submitted_by_email: string | null;
  assigned_to_user_id: string | null;
  subject: string;
  description: string;
  related_booking_id: string | null;
  related_amenity_id: string | null;
  related_unit_id: string | null;
  resolved_at: string | null;
  resolution_summary: string | null;
  created_at: string;
  updated_at: string;
  // joined
  submitter?: { full_name: string | null; email: string | null; role: string | null } | null;
  assignee?: { full_name: string | null; email: string | null; role: string | null } | null;
  unit?: { unit_number: string } | null;
  comment_count?: number;
}

export interface RequestComment {
  id: string;
  request_id: string;
  author_user_id: string | null;
  author_guest_token_id: string | null;
  author_name: string;
  body: string;
  is_internal: boolean;
  attachment_path: string | null;
  attachment_name: string | null;
  created_at: string;
}

// ─── Label helpers ────────────────────────────────────────────────────────────

export const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  maintenance: "Maintenance",
  dispute: "Dispute / Complaint",
  amenity: "Amenity Request",
  document: "Document Request",
  inquiry: "General Inquiry",
  compliance: "Compliance Issue",
};

export const REQUEST_TYPE_ICONS: Record<RequestType, string> = {
  maintenance: "🔧",
  dispute: "⚖️",
  amenity: "🏊",
  document: "📋",
  inquiry: "💬",
  compliance: "🚨",
};

export const REQUEST_STATUS_COLORS: Record<RequestStatus, string> = {
  new: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  acknowledged: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  in_progress: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  pending_info: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

export const PRIORITY_COLORS: Record<RequestPriority, string> = {
  low: "text-muted-foreground",
  normal: "text-foreground",
  high: "text-amber-600 dark:text-amber-400",
  urgent: "text-red-600 dark:text-red-400",
};

// ─── Fetch requests (inbox/outbox) ────────────────────────────────────────────

/** Fetch requests for the active org — adapts based on role */
export function useOrgRequests(filters?: {
  type?: RequestType;
  status?: RequestStatus;
  relationship?: RequestRelationship;
  assignedToMe?: boolean;
  submittedByMe?: boolean;
}) {
  const { activeOrgId, user } = useAuth();

  return useQuery({
    queryKey: ["requests", activeOrgId, filters],
    enabled: !!activeOrgId,
    queryFn: async () => {
      let q = supabase
        .from("requests")
        .select(`
          *,
          submitter:profiles!requests_submitted_by_user_id_fkey(full_name, email, role),
          assignee:profiles!requests_assigned_to_user_id_fkey(full_name, email, role),
          unit:units!requests_related_unit_id_fkey(unit_number)
        `)
        .eq("org_id", activeOrgId!)
        .order("created_at", { ascending: false });

      if (filters?.type) q = q.eq("type", filters.type);
      if (filters?.status) q = q.eq("status", filters.status);
      if (filters?.relationship) q = q.eq("context_relationship", filters.relationship);
      if (filters?.assignedToMe && user?.id) q = q.eq("assigned_to_user_id", user.id);
      if (filters?.submittedByMe && user?.id) q = q.eq("submitted_by_user_id", user.id);

      const { data, error } = await q;
      if (error) {
        console.warn("[useOrgRequests] query error:", error.message);
        return [] as Request[];
      }
      return (data ?? []) as Request[];
    },
  });
}

/** Fetch open (non-resolved) requests assigned to the current user */
export function useMyInboxRequests() {
  const { activeOrgId, user } = useAuth();
  return useQuery({
    queryKey: ["requests_inbox", activeOrgId, user?.id],
    enabled: !!activeOrgId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("requests")
        .select(`
          *,
          submitter:profiles!requests_submitted_by_user_id_fkey(full_name, email, role),
          unit:units!requests_related_unit_id_fkey(unit_number)
        `)
        .eq("org_id", activeOrgId!)
        .eq("assigned_to_user_id", user!.id)
        .not("status", "in", '("resolved","closed","rejected")')
        .order("created_at", { ascending: false });
      if (error) {
        console.warn("[useMyInboxRequests] error:", error.message);
        return [] as Request[];
      }
      return (data ?? []) as Request[];
    },
    refetchInterval: 60_000,
  });
}

/** Count of open requests in org by type — for dashboard badges */
export function useRequestCounts() {
  const { activeOrgId } = useAuth();
  return useQuery({
    queryKey: ["requests_counts", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("requests")
        .select("type, status")
        .eq("org_id", activeOrgId!)
        .not("status", "in", '("resolved","closed","rejected")');
      if (error) {
        console.warn("[useRequestCounts] error:", error.message);
        return { total: 0, maintenance: 0, dispute: 0, amenity: 0, document: 0, inquiry: 0, compliance: 0 };
      }
      const rows = data ?? [];
      const counts = { total: rows.length, maintenance: 0, dispute: 0, amenity: 0, document: 0, inquiry: 0, compliance: 0 };
      rows.forEach((r: any) => { if (r.type in counts) (counts as any)[r.type]++; });
      return counts;
    },
    refetchInterval: 30_000,
  });
}

// ─── Single request detail ────────────────────────────────────────────────────

export function useRequestDetail(requestId: string | null) {
  return useQuery({
    queryKey: ["request_detail", requestId],
    enabled: !!requestId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("requests")
        .select(`
          *,
          submitter:profiles!requests_submitted_by_user_id_fkey(full_name, email, role),
          assignee:profiles!requests_assigned_to_user_id_fkey(full_name, email, role),
          unit:units!requests_related_unit_id_fkey(unit_number)
        `)
        .eq("id", requestId!)
        .single();
      if (error) throw error;
      return data as Request;
    },
  });
}

// ─── Request comments ─────────────────────────────────────────────────────────

export function useRequestComments(requestId: string | null) {
  return useQuery({
    queryKey: ["request_comments", requestId],
    enabled: !!requestId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("request_comments")
        .select("*")
        .eq("request_id", requestId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as RequestComment[];
    },
    refetchInterval: 15_000,
  });
}

// ─── Create request ───────────────────────────────────────────────────────────

export function useCreateRequest() {
  const queryClient = useQueryClient();
  const { user, activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      type: RequestType;
      context_relationship: RequestRelationship;
      subject: string;
      description: string;
      priority?: RequestPriority;
      assigned_to_user_id?: string;
      related_booking_id?: string;
      related_amenity_id?: string;
      related_unit_id?: string;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");

      const { data, error } = await supabase
        .from("requests")
        .insert({
          ...payload,
          org_id: activeOrgId,
          submitted_by_user_id: user?.id ?? null,
          submitted_by_name: (user as any)?.fullName ?? user?.email ?? null,
          submitted_by_email: user?.email ?? null,
          priority: payload.priority ?? "normal",
          status: "new",
        })
        .select()
        .single();
      if (error) throw error;

      // Fire notification to assigned user or all managers
      if (payload.assigned_to_user_id) {
        await insertNotification({
          userId: payload.assigned_to_user_id,
          orgId: activeOrgId,
          type: "info",
          title: `New ${REQUEST_TYPE_LABELS[payload.type]} Request`,
          message: `${(user as any)?.fullName ?? "Someone"} submitted: "${payload.subject}"`,
          data: { link: `/requests?id=${data.id}`, request_id: data.id },
        });
      } else {
        await notifyOrgManagers(activeOrgId, {
          type: "info",
          title: `New ${REQUEST_TYPE_LABELS[payload.type]} Request`,
          message: `${(user as any)?.fullName ?? "A user"} submitted: "${payload.subject}"`,
          data: { link: `/requests?id=${data.id}`, request_id: data.id },
        });
      }

      return data as Request;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["requests", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["requests_counts", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["requests_inbox"] });
      toast({
        title: "Request submitted",
        description: `Your ${REQUEST_TYPE_LABELS[data.type].toLowerCase()} request has been submitted. You'll be notified when it's updated.`,
      });
    },
    onError: (e: Error) =>
      toast({ title: "Failed to submit request", description: e.message, variant: "destructive" }),
  });
}

// ─── Update request status ────────────────────────────────────────────────────

export function useUpdateRequestStatus() {
  const queryClient = useQueryClient();
  const { activeOrgId, user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      resolution_summary,
    }: {
      id: string;
      status: RequestStatus;
      resolution_summary?: string;
    }) => {
      const update: any = { status };
      if (status === "resolved" || status === "closed") {
        update.resolved_at = new Date().toISOString();
      }
      if (resolution_summary) update.resolution_summary = resolution_summary;

      const { data, error } = await supabase
        .from("requests")
        .update(update)
        .eq("id", id)
        .select("*, submitter:profiles!requests_submitted_by_user_id_fkey(full_name, email)")
        .single();
      if (error) throw error;

      // Notify the submitter
      if (data.submitted_by_user_id) {
        const statusLabel =
          status === "resolved" ? "Resolved ✓" :
          status === "rejected" ? "Rejected" :
          status === "in_progress" ? "In Progress" :
          status === "acknowledged" ? "Acknowledged" :
          status.replace(/_/g, " ");

        await insertNotification({
          userId: data.submitted_by_user_id,
          orgId: activeOrgId!,
          type: status === "resolved" ? "success" : status === "rejected" ? "error" : "info",
          title: `Request ${statusLabel}`,
          message: `"${data.subject}" has been updated to: ${statusLabel}.${resolution_summary ? ` Note: ${resolution_summary}` : ""}`,
          data: { link: `/requests?id=${id}`, request_id: id },
        });
      }

      return data as Request;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["requests", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["requests_counts", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["request_detail", data.id] });
      queryClient.invalidateQueries({ queryKey: ["requests_inbox"] });
      toast({ title: "Request updated" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

// ─── Add comment ──────────────────────────────────────────────────────────────

export function useAddRequestComment() {
  const queryClient = useQueryClient();
  const { user, activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      requestId,
      body,
      isInternal = false,
    }: {
      requestId: string;
      body: string;
      isInternal?: boolean;
    }) => {
      const authorName =
        (user as any)?.fullName ||
        user?.email ||
        "Unknown";

      const { data, error } = await supabase
        .from("request_comments")
        .insert({
          request_id: requestId,
          author_user_id: user?.id ?? null,
          author_name: authorName,
          body,
          is_internal: isInternal,
        })
        .select()
        .single();
      if (error) throw error;

      // Notify the other party about the reply
      const { data: req } = await supabase
        .from("requests")
        .select("submitted_by_user_id, assigned_to_user_id, subject")
        .eq("id", requestId)
        .single();

      if (req) {
        const notifyUserId =
          user?.id === req.submitted_by_user_id
            ? req.assigned_to_user_id
            : req.submitted_by_user_id;

        if (notifyUserId && !isInternal) {
          await insertNotification({
            userId: notifyUserId,
            orgId: activeOrgId!,
            type: "message",
            title: `New reply on: "${req.subject}"`,
            message: body.length > 120 ? body.slice(0, 117) + "…" : body,
            data: { link: `/requests?id=${requestId}`, request_id: requestId },
          });
        }
      }

      return data as RequestComment;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["request_comments", data.request_id] });
    },
    onError: (e: Error) =>
      toast({ title: "Failed to add comment", description: e.message, variant: "destructive" }),
  });
}

// ─── Assign request ───────────────────────────────────────────────────────────

export function useAssignRequest() {
  const queryClient = useQueryClient();
  const { activeOrgId, user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, assigneeId }: { id: string; assigneeId: string }) => {
      const { data, error } = await supabase
        .from("requests")
        .update({ assigned_to_user_id: assigneeId, status: "acknowledged" })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;

      await insertNotification({
        userId: assigneeId,
        orgId: activeOrgId!,
        type: "info",
        title: "Request assigned to you",
        message: `"${data.subject}" has been assigned to you for handling.`,
        data: { link: `/requests?id=${id}`, request_id: id },
      });
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["requests", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["request_detail", data.id] });
      toast({ title: "Request assigned" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}
